import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

type Procedimento = { id: string; name: string; color: string | null; especialidade: { code: string; label: string } | null };

const NEUTRA = "#A1A1AA";

/**
 * Procedimentos que o profissional atende, agrupados pela especialidade principal. Pílula neutra
 * (marcada em azul) e só a bolinha na cor do procedimento — a cor identifica, não decora. As
 * especialidades do próprio profissional vêm primeiro.
 */
export function ResponsabilidadesDoMembro({
  procedimentos,
  marcados,
  especialidadesDoMembro,
  podeEditar,
  onAlternar,
}: {
  procedimentos: Procedimento[];
  marcados: string[];
  especialidadesDoMembro: string[];
  podeEditar: boolean;
  onAlternar: (id: string) => void;
}) {
  if (!procedimentos.length) return <p className="text-[12px] text-zinc-500">A clínica ainda não tem procedimentos no catálogo.</p>;
  const grupos = new Map<string, { label: string; codigo: string; itens: Procedimento[] }>();
  for (const p of procedimentos) {
    const codigo = p.especialidade?.code ?? "";
    const g = grupos.get(codigo) ?? { label: p.especialidade?.label ?? "Outros", codigo, itens: [] };
    g.itens.push(p);
    grupos.set(codigo, g);
  }
  const ordem = [...grupos.values()].sort(
    (a, b) => Number(especialidadesDoMembro.includes(b.codigo)) - Number(especialidadesDoMembro.includes(a.codigo)) || a.label.localeCompare(b.label, "pt-BR"),
  );
  const sel = new Set(marcados);

  return (
    <div className="space-y-3">
      {ordem.map((g) => (
        <div key={g.codigo}>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{g.label}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {g.itens.map((p) => {
              const ativo = sel.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={!podeEditar}
                  aria-pressed={ativo}
                  onClick={() => onAlternar(p.id)}
                  className={cn(
                    "flex min-h-8 items-center gap-2 rounded-full border px-3 py-1 text-left text-[12px] font-medium transition-colors disabled:cursor-default",
                    ativo
                      ? "border-brand-300 bg-brand-50 text-brand-800 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                      : "border-zinc-200 bg-white text-zinc-700 enabled:hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground",
                  )}
                >
                  {ativo ? <Check className="h-3 w-3 shrink-0 text-brand-600" strokeWidth={3} /> : null}
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: p.color || NEUTRA }} />
                  <span className="min-w-0 truncate" title={p.name}>
                    {p.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
